import React, { useMemo, useState } from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  formatCompactNumber,
  formatLatency,
  formatMoney,
  formatNumber,
  formatPercent,
  humanizeTraceName,
  shareOf,
} from "../format";
import { internalOrgReason, isInternalOrg } from "../internalOrgs";

const SERIES_COLORS = [
  "#4f46e5",
  "#0ea5e9",
  "#10b981",
  "#f59e0b",
  "#ec4899",
  "#8b5cf6",
  "#14b8a6",
  "#ef4444",
];

/**
 * Rows come back from `aggregateBy` keyed on `key`/`label`. A missing key means
 * the trace carried no org/model tag at all — that is real, billable traffic we
 * cannot attribute, so it is surfaced as "Unattributed" rather than dropped.
 */
const rowLabel = (row, names = {}) => {
  const key = row?.key;
  if (!key || key === "Unknown") return "Unattributed";
  return names[key] || row.label || key;
};

function MiniTrend({ trend, color = "#4f46e5" }) {
  const data = useMemo(
    () =>
      (trend || []).map((point) => ({
        t: point.timestamp || point.key,
        cost: point.totalCost || 0,
      })),
    [trend],
  );

  if (data.length < 2) {
    return <div className="co-trend-empty">Not enough points to trend</div>;
  }

  return (
    <ResponsiveContainer width="100%" height={90}>
      <AreaChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
        <defs>
          <linearGradient id={`co-grad-${color.slice(1)}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.45} />
            <stop offset="100%" stopColor={color} stopOpacity={0.02} />
          </linearGradient>
        </defs>
        <Tooltip
          formatter={(value) => [formatMoney(value), "Cost"]}
          labelFormatter={(label) =>
            label ? new Date(label).toLocaleString() : ""
          }
          contentStyle={{ fontSize: 12, borderRadius: 8 }}
        />
        <Area
          type="monotone"
          dataKey="cost"
          stroke={color}
          strokeWidth={2}
          fill={`url(#co-grad-${color.slice(1)})`}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

/**
 * Per-feature client list. This is the "which customers drive this cost"
 * question, and it is the one place the internal-org toggle actually changes
 * numbers rather than just hiding rows — so the excluded total is always
 * reported back, never silently dropped.
 */
function ClientTable({ organizations, orgNames, excludeInternal, featureCost }) {
  const { rows, excludedCost, excludedCount } = useMemo(() => {
    const all = organizations || [];
    let cost = 0;
    let count = 0;
    const kept = [];
    all.forEach((org) => {
      const name = orgNames[org.key];
      if (excludeInternal && isInternalOrg(org.key, name)) {
        cost += org.totalCost || 0;
        count += 1;
        return;
      }
      kept.push(org);
    });
    return { rows: kept, excludedCost: cost, excludedCount: count };
  }, [organizations, orgNames, excludeInternal]);

  if (!rows.length) {
    return (
      <p className="co-empty">
        No client attribution for this feature in this window.
        {excludedCount > 0
          ? ` All ${excludedCount} attributed org(s) were Addy-internal.`
          : " Traces are missing an org: tag."}
      </p>
    );
  }

  const visibleTotal = rows.reduce((sum, r) => sum + (r.totalCost || 0), 0);

  return (
    <>
      <table className="ou-table co-inner-table">
        <thead>
          <tr>
            <th>Client</th>
            <th>Cost</th>
            <th>Share</th>
            <th>Runs</th>
            <th>Avg $/run</th>
            <th>Tokens</th>
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, 15).map((org) => {
            const name = orgNames[org.key];
            const reason = internalOrgReason(org.key, name);
            const runs = org.traceCount || 0;
            return (
              <tr key={org.key || "unattributed"}>
                <td title={org.key}>
                  {rowLabel(org, orgNames)}
                  {reason && !excludeInternal ? (
                    <span className="co-internal-tag" title={reason}>
                      internal
                    </span>
                  ) : null}
                </td>
                <td>{formatMoney(org.totalCost)}</td>
                <td>
                  <div className="co-share">
                    <div
                      className="co-share-bar"
                      style={{
                        width: `${Math.min(shareOf(org.totalCost, visibleTotal) * 100, 100)}%`,
                      }}
                    />
                    <span>{formatPercent(shareOf(org.totalCost, visibleTotal))}</span>
                  </div>
                </td>
                <td>{formatNumber(runs)}</td>
                <td>{runs ? formatMoney((org.totalCost || 0) / runs) : "—"}</td>
                <td>{formatCompactNumber(org.totalTokens)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {excludeInternal && excludedCount > 0 ? (
        <p className="co-note">
          Excluded {excludedCount} Addy-internal org(s) worth{" "}
          <strong>{formatMoney(excludedCost)}</strong> (
          {formatPercent(shareOf(excludedCost, featureCost))} of this feature).
        </p>
      ) : null}
      {rows.length > 15 ? (
        <p className="co-note">
          Showing top 15 of {rows.length} clients.
        </p>
      ) : null}
    </>
  );
}

/** Models used inside one feature, from the traceName x model breakdown. */
function ModelTable({ models, featureCost }) {
  if (!models?.length) {
    return (
      <p className="co-empty">
        No model attribution — traces for this feature carry no model tag.
      </p>
    );
  }

  const chartData = models.slice(0, 8).map((m) => ({
    name: m.secondaryKey || "Unknown",
    cost: m.totalCost || 0,
  }));

  return (
    <>
      <ResponsiveContainer width="100%" height={Math.max(chartData.length * 30, 90)}>
        <BarChart data={chartData} layout="vertical" margin={{ left: 8, right: 16 }}>
          <CartesianGrid strokeDasharray="3 3" horizontal={false} opacity={0.25} />
          <XAxis type="number" tickFormatter={(v) => formatMoney(v, { compact: true })} fontSize={11} />
          <YAxis type="category" dataKey="name" width={150} fontSize={11} />
          <Tooltip formatter={(value) => [formatMoney(value), "Cost"]} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
          <Bar dataKey="cost" radius={[0, 4, 4, 0]}>
            {chartData.map((entry, i) => (
              <Cell key={entry.name} fill={SERIES_COLORS[i % SERIES_COLORS.length]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <table className="ou-table co-inner-table">
        <thead>
          <tr>
            <th>Model</th>
            <th>Cost</th>
            <th>Share of feature</th>
            <th>Runs</th>
            <th>Tokens</th>
          </tr>
        </thead>
        <tbody>
          {models.map((m) => (
            <tr key={m.secondaryKey || m.key}>
              <td>{m.secondaryKey || "Unknown"}</td>
              <td>{formatMoney(m.totalCost)}</td>
              <td>{formatPercent(shareOf(m.totalCost, featureCost))}</td>
              <td>{formatNumber(m.traceCount)}</td>
              <td>{formatCompactNumber(m.totalTokens)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

function FeatureDetail({ feature, models, orgNames, excludeInternal }) {
  const [tab, setTab] = useState("clients");
  const cost = feature.summary?.totalCost || 0;

  return (
    <div className="co-feature-detail">
      <div className="co-detail-tabs">
        <button
          type="button"
          className={tab === "clients" ? "is-active" : ""}
          onClick={() => setTab("clients")}
        >
          Clients
        </button>
        <button
          type="button"
          className={tab === "models" ? "is-active" : ""}
          onClick={() => setTab("models")}
        >
          Models
        </button>
        <button
          type="button"
          className={tab === "loans" ? "is-active" : ""}
          onClick={() => setTab("loans")}
        >
          Loans
        </button>
        <button
          type="button"
          className={tab === "trend" ? "is-active" : ""}
          onClick={() => setTab("trend")}
        >
          Trend
        </button>
      </div>

      {tab === "clients" ? (
        <ClientTable
          organizations={feature.topOrganizations}
          orgNames={orgNames}
          excludeInternal={excludeInternal}
          featureCost={cost}
        />
      ) : null}

      {tab === "models" ? <ModelTable models={models} featureCost={cost} /> : null}

      {tab === "loans" ? (
        feature.topLoans?.length ? (
          <table className="ou-table co-inner-table">
            <thead>
              <tr>
                <th>Loan</th>
                <th>Cost</th>
                <th>Runs</th>
                <th>Orgs</th>
              </tr>
            </thead>
            <tbody>
              {feature.topLoans.slice(0, 15).map((loan) => (
                <tr key={loan.key}>
                  <td title={loan.key}>{loan.key || "Unattributed"}</td>
                  <td>{formatMoney(loan.totalCost)}</td>
                  <td>{formatNumber(loan.traceCount)}</td>
                  <td>{formatNumber(loan.organizationCount ?? 1)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="co-empty">No loan attribution for this feature.</p>
        )
      ) : null}

      {tab === "trend" ? <MiniTrend trend={feature.trend} /> : null}
    </div>
  );
}

export function AiBreakdown({
  data,
  modelMatrix,
  orgNames,
  excludeInternal,
  envLabel,
}) {
  const [expanded, setExpanded] = useState(null);

  const features = useMemo(() => {
    const byTraceName = data?.byTraceName || {};
    return Object.values(byTraceName)
      .map((entry) => ({
        ...entry,
        cost: entry.summary?.totalCost || 0,
      }))
      .sort((a, b) => b.cost - a.cost);
  }, [data]);

  // traceName -> its model rows, from the two-dimensional breakdown.
  const modelsByFeature = useMemo(() => {
    const map = {};
    (modelMatrix || []).forEach((row) => {
      const key = row.primaryKey;
      if (!key) return;
      if (!map[key]) map[key] = [];
      map[key].push(row);
    });
    Object.values(map).forEach((rows) =>
      rows.sort((a, b) => (b.totalCost || 0) - (a.totalCost || 0)),
    );
    return map;
  }, [modelMatrix]);

  const totalCost = data?.summary?.totalCost || 0;

  if (!features.length) {
    return (
      <p className="co-empty">
        No AI traces found in {envLabel} for this window.
      </p>
    );
  }

  return (
    <div className="ou-table-wrap">
      <table className="ou-table co-feature-table">
        <thead>
          <tr>
            <th style={{ width: 28 }} />
            <th>Feature</th>
            <th>Cost</th>
            <th>Share</th>
            <th>Runs</th>
            <th>Avg $/run</th>
            <th>Tokens</th>
            <th>p95</th>
            <th>Errors</th>
            <th>Clients</th>
          </tr>
        </thead>
        <tbody>
          {features.map((feature) => {
            const isOpen = expanded === feature.traceName;
            const s = feature.summary || {};
            const runs = s.traceCount || 0;
            return (
              <React.Fragment key={feature.traceName}>
                <tr
                  className="is-clickable"
                  onClick={() => setExpanded(isOpen ? null : feature.traceName)}
                >
                  <td>
                    {isOpen ? (
                      <ExpandMoreIcon fontSize="small" />
                    ) : (
                      <ChevronRightIcon fontSize="small" />
                    )}
                  </td>
                  <td title={feature.traceName}>
                    {humanizeTraceName(feature.traceName)}
                  </td>
                  <td className="co-strong">{formatMoney(feature.cost)}</td>
                  <td>
                    <div className="co-share">
                      <div
                        className="co-share-bar"
                        style={{
                          width: `${Math.min(shareOf(feature.cost, totalCost) * 100, 100)}%`,
                        }}
                      />
                      <span>{formatPercent(shareOf(feature.cost, totalCost))}</span>
                    </div>
                  </td>
                  <td>{formatNumber(runs)}</td>
                  <td>{runs ? formatMoney(feature.cost / runs) : "—"}</td>
                  <td>{formatCompactNumber(s.totalTokens)}</td>
                  <td>{formatLatency(s.p95Latency)}</td>
                  <td className={s.errorRate > 0.05 ? "co-err" : ""}>
                    {formatPercent(s.errorRate || 0, 1)}
                  </td>
                  <td>{formatNumber(s.uniqueOrganizations)}</td>
                </tr>
                {isOpen ? (
                  <tr className="co-detail-row">
                    <td colSpan={10}>
                      <FeatureDetail
                        feature={feature}
                        models={modelsByFeature[feature.traceName]}
                        orgNames={orgNames}
                        excludeInternal={excludeInternal}
                      />
                    </td>
                  </tr>
                ) : null}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
